import { useId, type ReactNode } from 'react';
import type { FieldError } from 'react-hook-form';
import { FormFieldContext, type FormFieldContextValue } from './form-field-context';
import styles from './FormField.module.css';

export type FormFieldProps = {
  /** CEVRILMIS etiket. Modul seviyesinde sabit tutuyorsan `labelKey` sakla, burada t() ile ver. */
  label: ReactNode;
  /**
   * react-hook-form'un `formState.errors.alan` degeri dogrudan verilebilir.
   * Mesaj zaten sema fabrikasindan (makeXSchema(t)) cevrilmis gelir.
   */
  error?: FieldError | string | undefined;
  hint?: ReactNode | undefined;
  required?: boolean;
  children: ReactNode;
  className?: string | undefined;
};

const errorMessage = (error: FormFieldProps['error']): string | undefined => {
  if (!error) return undefined;
  if (typeof error === 'string') return error;
  return error.message;
};

/**
 * Etiket + kontrol + hata ucusu. Icindeki Input/Select id ve aria baglarini
 * context'ten alir; cagri yerinde id uydurmak gerekmez.
 */
export function FormField({
  label,
  error,
  hint,
  required = false,
  children,
  className,
}: FormFieldProps) {
  const reactId = useId();
  const controlId = `${reactId}-control`;
  const errorId = `${reactId}-error`;
  const hintId = `${reactId}-hint`;

  const message = errorMessage(error);
  const describedBy = [message ? errorId : undefined, hint ? hintId : undefined]
    .filter(Boolean)
    .join(' ');

  const contextValue: FormFieldContextValue = {
    controlId,
    describedBy: describedBy || undefined,
    invalid: message !== undefined,
  };

  return (
    <div className={[styles.field, className].filter(Boolean).join(' ')}>
      <label className={styles.label} htmlFor={controlId}>
        {label}
        {required ? (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      <FormFieldContext value={contextValue}>{children}</FormFieldContext>

      {hint ? (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      ) : null}
      {message ? (
        <p className={styles.error} id={errorId} role="alert">
          {message}
        </p>
      ) : null}
    </div>
  );
}
